import { addDoc, collection, getDocs, query, where, updateDoc, serverTimestamp } from "firebase/firestore";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Search, X, ChevronLeft, ChevronRight, Eye, Users, CheckSquare, Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import { getMessaging, getToken } from "firebase/messaging";
import { normalizeReraFromRaw, normalizeHaryanaFromRaw } from "../utils/normalizeRera";
import featureGatingService from "../../services/featureGatingService";
import { toast } from "react-toastify";



function PdfLinksCell({ value }) {
  const [expanded, setExpanded] = React.useState(false);

  if (!Array.isArray(value) || value.length === 0) {
    return <span>—</span>;
  }

  const visibleLinks = expanded ? value : value.slice(0, 2);

  return (
    <div className="flex flex-col gap-1">
      {visibleLinks.map((item, idx) => (
        <a
          key={idx}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start text-blue-600 hover:text-blue-700 underline"
        >
          <img
            src={`${process.env.PUBLIC_URL}/pdf-icon.png`}
            alt="PDF"
            className="w-4 h-4 mr-2 mt-1"
          />
          <span className="break-words">{item.text}</span>
        </a>
      ))}

      {value.length > 2 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-yellow-600 hover:text-yellow-700 underline mt-1 text-left"
        >
          {expanded
            ? "View less"
            : `View more (${value.length - 2} more)`}
        </button>
      )}
    </div>
  );
}

function ReraCaseList() {
  const fetchFullReraCaseDetails = async (filingNo) => {
  const API_BASE = "https://jreraa.valscotech.com";

  const getSessionId = () =>
    sessionStorage.getItem("upReraSessionId") ||
    localStorage.getItem("upReraSessionId");

  let sessionId = getSessionId();

  // 🔁 Ensure session
  if (!sessionId) {
    const capRes = await fetch(`${API_BASE}/api/rera/up/generateCaptcha`);
    const capData = await capRes.json();
    sessionId = capData.sessionId;

    sessionStorage.setItem("upReraSessionId", sessionId);
    localStorage.setItem("upReraSessionId", sessionId);
  }

  const res = await fetch(
    `${API_BASE}/api/rera/up/getCaseDetails`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, filingNo }),
    }
  );

  const json = await res.json();

  if (!res.ok || !json.success) {
    console.error("RERA DETAILS ERROR:", json);
    throw new Error("Failed to fetch RERA full case details");
  }

  return json.data;
};

const fetchHaryanaCaseDetails = async (internalId) => {
  const HARYANA_API = "https://rera-haryana-596718606544.asia-south1.run.app";

  const sessionId =
    sessionStorage.getItem("haryanaReraSessionId") ||
    localStorage.getItem("haryanaReraSessionId");

  if (!sessionId) throw new Error("Haryana session expired. Please search again.");

  const res = await fetch(`${HARYANA_API}/api/rera/hr/getCaseDetails`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId, internalId }),
  });

  const json = await res.json();

  if (!res.ok || !json.success) {
    console.error("HARYANA DETAILS ERROR:", json);
    throw new Error("Failed to fetch Haryana case details");
  }

  return json.data;
};

const [searchText, setSearchText] = useState("");

 const renderPdfLinks = (value) => {
  if (!Array.isArray(value) || value.length === 0) return "—";

  return (
    <div className="flex flex-col gap-1">
      {value.map((item, idx) => (
        <a
          key={idx}
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start text-blue-600 hover:text-blue-700 underline"
        >
          <img
            src={`${process.env.PUBLIC_URL}/pdf-icon.png`}
            alt="PDF"
            className="w-4 h-4 mr-2 mt-1"
          />
          <span className="break-words">{item.text}</span>
        </a>
      ))}
    </div>
  );
};


    const messaging = getMessaging();
  const location = useLocation();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    total: 0,
    exported: 0,
    currentCase: null,
    startTime: null,
    failedCases: []
  });
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const showInfo = () => setIsInfoModalOpen(true);
  const closeInfoModal = () => setIsInfoModalOpen(false);

  useEffect(() => {
  setIsLoadingData(true);

  if (location.state?.data) {
    setCaseData(location.state.data);
  } else {
    setCaseData(null);
  }

  setIsLoadingData(false);
}, [location.state]);


const cases = (caseData?.rows || []).filter(row => {
  if (!searchText.trim()) return true;

  const search = searchText.toLowerCase();

  return Object.values(row).some(value =>
    String(value).toLowerCase().includes(search)
  );
});


const apiHeaders = caseData?.headers;

const headers = caseData?.headers || [];

// Normalize a header/key so "Case No." and "caseNo" compare equal.
const normalizeKey = (str) =>
  String(str).toLowerCase().replace(/\s+/g, "").replace(/[^\w]/g, "");



  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage] = useState(10);

  const indexOfLastCase = currentPage * casesPerPage;
  const indexOfFirstCase = indexOfLastCase - casesPerPage;
  const currentCases = cases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.ceil(cases.length / casesPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePreviousPage = () => currentPage > 1 && handlePageChange(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && handlePageChange(currentPage + 1);



          
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const handleCaseSelect = (index) => {
    const newSelectedCases = new Set(selectedCases);
    if (newSelectedCases.has(index)) {
      newSelectedCases.delete(index);
    } else {
      newSelectedCases.add(index);
    }
    setSelectedCases(newSelectedCases);
    setSelectAll(newSelectedCases.size === cases.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases(new Set());
      setSelectAll(false);
    } else {
      const allIndices = new Set(Array.from({ length: cases.length }, (_, i) => i));
      setSelectedCases(allIndices);
      setSelectAll(true);
    }
  };

  const handlePageSelectAll = () => {
    const currentPageIndices = currentCases.map((_, index) => indexOfFirstCase + index);
    const newSelectedCases = new Set(selectedCases);
    const allCurrentSelected = currentPageIndices.every(index => selectedCases.has(index));
    
    if (allCurrentSelected) {
      currentPageIndices.forEach(index => newSelectedCases.delete(index));
    } else {
      currentPageIndices.forEach(index => newSelectedCases.add(index));
    }
    
    setSelectedCases(newSelectedCases);
    setSelectAll(newSelectedCases.size === cases.length);
  };



  const exportSelectedCases = async () => {



  try {
    const user = auth.currentUser;
    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const selectedCaseIndices = Array.from(selectedCases || []);
    if (selectedCaseIndices.length === 0) {
      toast.info("No cases selected to export");
      return;
    }

    setExporting(true);
    setExportProgress({
      total: selectedCaseIndices.length,
      exported: 0,
      currentCase: "Preparing to export RERA cases...",
      startTime: Date.now(),
      failedCases: []
    });

    await new Promise(r => setTimeout(r, 100));

    let successfulExports = 0;
    let failedExports = 0;
    const duplicateCases = [];
    let eventsCreated = 0;

    // Get current pending count for subscription metadata
    const pendingSnap0 = await getDocs(query(collection(db, "pending"), where("owner", "==", user.uid)));
    let currentPendingCount = pendingSnap0.size;

    for (const index of selectedCaseIndices) {
  const caseItem = cases[index];
  if (!caseItem) continue;

  // Delhi cases: extract caseNo directly from list data.
  // The list mapper writes a "Case No" header key while the raw RERA API row
  // uses "caseNo". Older endpoints occasionally use "Case No." with a period.
  const delhiCaseNoRaw =
    caseItem?.caseNo ??
    caseItem?.["Case No"] ??
    caseItem?.["Case No."] ??
    caseItem?.["caseNo"] ??
    "";
  const delhiCaseNo = Array.isArray(delhiCaseNoRaw)
    ? delhiCaseNoRaw[0]?.text || ""
    : delhiCaseNoRaw;

  const filingNo =
    caseItem?.Action?.filingNo ||
    caseItem?.action?.filingNo ||
    caseItem?.["Complaint ID"] ||
    delhiCaseNo ||
    "";
  const internalId =
    caseItem?.Action?.internalId ||
    caseItem?.action?.internalId ||
    "";
  if (!filingNo && !internalId) {
    console.warn("Skipping case — missing filingNo/internalId", caseItem);
    continue;
  }

  // Extract petitioner and respondent from Case Title (format: "Petitioner VS Respondent")
  const caseTitle = caseItem["Case Title"] || caseItem.caseTitle || "";
  const titleParts = caseTitle.split(/\s+VS\s+/i);
  const petitionerFromTitle = titleParts[0]?.trim() || "";
  const respondentFromTitle = titleParts[1]?.trim() || "";

  // For Delhi agent cases: "Respondent Agent Details" is the respondent, petitioner is N/A
  // For Delhi complaint cases: "complainant" is petitioner, "respondent" is respondent
  const isAgentCase = !!caseItem["Respondent Agent Details"];

  const petitioner =
    petitionerFromTitle ||
    caseItem["Complainant"] ||
    caseItem.complainant ||
    (isAgentCase ? "N/A" : "N/A");

  const respondent =
    respondentFromTitle ||
    caseItem["Respondent"] ||
    caseItem.respondent ||
    caseItem["Respondent Agent Details"] ||
    "N/A";

  try {
    setExportProgress(p => ({
      ...p,
      currentCase: `Checking: ${filingNo}`
    }));

    // DUPLICATE CHECK FIRST
    const pendingQ = query(
      collection(db, "pending"),
      where("filingNo", "==", filingNo),
      where("owner", "==", user.uid)
    );

    const disposedQ = query(
      collection(db, "disposed"),
      where("filingNo", "==", filingNo),
      where("owner", "==", user.uid)
    );

    const [pendingSnap, disposedSnap] = await Promise.all([
      getDocs(pendingQ),
      getDocs(disposedQ),
    ]);

    if (!pendingSnap.empty || !disposedSnap.empty) {
      duplicateCases.push(filingNo);
      setExportProgress(p => ({
        ...p,
        exported: p.exported + 1,
        currentCase: `Skipped duplicate: ${filingNo}`
      }));
      continue;
    }

    setExportProgress(p => ({
      ...p,
      currentCase: `Exporting: ${filingNo || internalId}`
    }));

    let caseDataToExport;
    let actualCaseNo;
    let fullDetails;

    if (stateCode === "DELHI") {
      // Delhi: build case data directly from the list row
      actualCaseNo = delhiCaseNo || filingNo;

      // Extract notices and orders from the row
      const notices = Array.isArray(caseItem.Notice) ? caseItem.Notice
        : Array.isArray(caseItem["Copy of Notice"]) ? caseItem["Copy of Notice"]
        : [];
      const orders = Array.isArray(caseItem.Order) ? caseItem.Order
        : Array.isArray(caseItem["Order Passed"]) ? caseItem["Order Passed"]
        : [];

      const nextHearing = caseItem.nextHearing || caseItem["Next Date of Hearing"] || caseItem["Next Hearing"] || "";
      const lastHearing = caseItem.lastHearing || caseItem["Date (Last Hearing / Reserved Order)"] || caseItem["Last Hearing"] || "";
      const filedOn = caseItem.filedOn || caseItem["Filed On"] || "";

      caseDataToExport = {
        filingNo: actualCaseNo,
        caseNumber: actualCaseNo,
        caseNo: actualCaseNo,
        caseType: "RERA",
        court: "RERA",
        courtName: "RERA Delhi",
        petitionerName: petitioner,
        respondentName: respondent,
        nextHearingDate: nextHearing,
        lastHearingDate: lastHearing,
        caseFiledDate: filedOn,
        status: "pending",
        allPetitioners: [{ name: petitioner }],
        allRespondents: [{ name: respondent }],
        rawReraData: { delhiListRow: caseItem },
        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
      };

      fullDetails = { notices, orders };
    } else if (stateCode === "HARYANA" && internalId) {
      fullDetails = await fetchHaryanaCaseDetails(internalId);
      const normalized = normalizeHaryanaFromRaw(fullDetails);
      actualCaseNo = normalized.caseNumber || filingNo;

      caseDataToExport = {
        ...normalized,
        filingNo: filingNo || actualCaseNo,
        caseNumber: actualCaseNo,
        caseNo: actualCaseNo,
        petitionerName: petitioner,
        respondentName: respondent,
        rawReraData: fullDetails,
        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
      };
    } else {
      fullDetails = await fetchFullReraCaseDetails(filingNo);
      const normalized = normalizeReraFromRaw(fullDetails.sections || []);

      const caseDetailSection = fullDetails.sections?.find(
        s => s.title?.toLowerCase().includes("case detail") && s.type === "key-value"
      );
      const caseNoFromSection = caseDetailSection?.data?.["Case No"]
        || caseDetailSection?.data?.["Case No."]
        || caseDetailSection?.data?.["CaseNo"]
        || Object.entries(caseDetailSection?.data || {}).find(([k]) => k.toLowerCase().replace(/[^a-z]/g, '') === 'caseno')?.[1];
      actualCaseNo = caseNoFromSection || filingNo;

      caseDataToExport = {
        ...normalized,
        filingNo,
        caseNumber: actualCaseNo,
        caseNo: actualCaseNo,
        petitionerName: petitioner,
        respondentName: respondent,
        rawReraData: {
          sections: fullDetails.sections || [],
          orders: fullDetails.orders || [],
        },
        owner: user.uid,
        team: [user.uid],
        createdAt: new Date(),
      };
    }

const targetCollection = caseDataToExport.status || "pending";

// Get subscription metadata for pending cases
let subscriptionMetadata = null;
if (targetCollection === "pending") {
  subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
    user.uid,
    currentPendingCount
  );
}

// Add payload, metadata and timestamp
caseDataToExport.payload = {
  exportedAt: new Date().toISOString(),
  caseexportedfrom: "RERA",
};
if (subscriptionMetadata) {
  caseDataToExport.subscriptionMetadata = subscriptionMetadata;
}
caseDataToExport.timestamp = serverTimestamp();

// Check case limit before proceeding
const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
  user.uid,
  1,
  targetCollection === "disposed" ? "disposed" : "pending"
);

if (!limitResult.canProceed) {
  toast.error(`Case limit reached! Your ${limitResult.planName} plan allows ${limitResult.maxLimit} ${targetCollection} cases. Skipping remaining cases.`);
  failedExports++;
  break; // Stop exporting further cases
}

const docRef = await addDoc(
  collection(db, targetCollection),
  caseDataToExport
);

const caseId = docRef.id;

await updateDoc(docRef, {
  caseFilesLink: `/portal/case-files/${caseId}?from=rera`,
});

// Create hearing reminder events (only if next hearing date is in the future)
try {
  const nextHearingRaw = caseDataToExport.nextHearingDate;
  const parseHearingDate = (str) => {
    if (!str) return null;
    // Delhi RERA returns values like "08/06/2026  11:30 AM" so accept an
    // optional trailing time/AM-PM portion.
    const trimmed = String(str).trim();
    const iso = trimmed.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:\b|$)/);
    if (iso) return new Date(parseInt(iso[1]), parseInt(iso[2]) - 1, parseInt(iso[3]));
    const dmy = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\b|$)/);
    if (dmy) return new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
    return null;
  };
  const formatYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const eventDate = parseHearingDate(nextHearingRaw);
  if (eventDate && !isNaN(eventDate.getTime())) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hearingDay = new Date(eventDate);
    hearingDay.setHours(0, 0, 0, 0);

    if (hearingDay >= today) {
      const reminderDate = new Date(eventDate);
      reminderDate.setHours(8, 0, 0, 0);
      const eventTimeIST = new Date(eventDate);
      eventTimeIST.setHours(18, 0, 0, 0);
      const now = new Date();

      if (reminderDate > now) {
        const reminderEventData = {
          "Case No": actualCaseNo,
          date: formatYMD(reminderDate),
          id: Math.floor(Math.random() * 90000) + 10000,
          time: "08:00 AM",
          time1: reminderDate.getTime(),
          title: `${petitioner} VS ${respondent}`,
          description: `Hearing Reminder : Case No. - ${actualCaseNo}`,
          isScheduled: true,
          status: targetCollection,
          eventType: "reminder",
        };
        const reminderEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), reminderEventData);
        await addDoc(collection(db, "eventReminders"), {
          eventId: reminderEventRef.id,
          reminderTime: reminderDate,
          eventTitle: `Hearing Reminder : Case No. - ${actualCaseNo}`,
          caseNo: actualCaseNo,
          caseId: caseId,
          collection: targetCollection,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: user.uid,
          recipientId: user.uid,
        });
      }

      if (eventTimeIST > now) {
        const mainEventData = {
          "Case No": actualCaseNo,
          date: formatYMD(eventDate),
          id: Math.floor(Math.random() * 90000) + 10000,
          time: "06:00 PM",
          time1: eventTimeIST.getTime(),
          title: `Update Next Hearing: ${petitioner} VS ${respondent}`,
          description: `Case No. - ${actualCaseNo}`,
          isScheduled: true,
          status: targetCollection,
          eventType: "updateReminder",
          isHidden: true,
        };
        const mainEventRef = await addDoc(collection(db, "lawyers", user.uid, "events"), mainEventData);
        await addDoc(collection(db, "eventReminders"), {
          eventId: mainEventRef.id,
          reminderTime: eventTimeIST,
          eventTitle: `Update Next Hearing : ${actualCaseNo}`,
          caseNo: actualCaseNo,
          caseId: caseId,
          collection: targetCollection,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: user.uid,
          recipientId: user.uid,
        });
      }
    } else {
      console.log("RERA: skipping event creation — next hearing date is in the past:", nextHearingRaw);
    }
  }
} catch (notificationError) {
  console.error("RERA notification setup error:", notificationError);
}

// Save notice and order files
if (stateCode === "DELHI") {
  // Save notices to "notices" folder
  const notices = fullDetails?.notices || [];
  for (const n of notices) {
    if (!n?.link) continue;
    await addDoc(collection(db, "files"), {
      caseNo: actualCaseNo,
      filingNo: actualCaseNo,
      fileName: n.text || `Notice – ${actualCaseNo}`,
      file_url: n.link,
      lawyer_uid: user.uid,
      uploadedAt: new Date().toISOString(),
      source: "RERA",
      folder: "notices",
    });
  }
  // Save orders to "orders" folder
  const orders = fullDetails?.orders || [];
  for (const o of orders) {
    if (!o?.link) continue;
    await addDoc(collection(db, "files"), {
      caseNo: actualCaseNo,
      filingNo: actualCaseNo,
      fileName: o.text || `Order – ${actualCaseNo}`,
      file_url: o.link,
      lawyer_uid: user.uid,
      uploadedAt: new Date().toISOString(),
      source: "RERA",
      folder: "orders",
    });
  }
} else if (stateCode === "HARYANA") {
  const listings = fullDetails?.sections?.complaint_listing_details || [];
  for (const hearing of listings) {
    if (!hearing?.Order_action?.url) continue;
    await addDoc(collection(db, "files"), {
      caseNo: actualCaseNo,
      filingNo: filingNo || actualCaseNo,
      fileName: `Order_dated_on_${hearing["Date of Hearing"] || "Unknown Date"}`,
      file_url: hearing.Order_action.url,
      lawyer_uid: user.uid,
      uploadedAt: hearing["Order Uploaded On"] || new Date().toISOString(),
      source: "RERA",
      folder: "orders",
    });
  }
} else if (Array.isArray(fullDetails.orders)) {
  for (const o of fullDetails.orders) {
    if (!o?.link) continue;
    await addDoc(collection(db, "files"), {
      caseNo: actualCaseNo,
      filingNo: filingNo,
      fileName: o.title || `RERA Order – ${o.date || "Unknown Date"}`,
      file_url: o.link,
      lawyer_uid: user.uid,
      uploadedAt: o.date || new Date().toISOString(),
      source: "RERA",
    });
  }
}



    console.log("EXPORTING NORMALIZED:", caseDataToExport);



//    const caseDataToExport = {
//   // 🔹 REQUIRED FOR CaseDetails PAGE
//   caseNumber: filingNo,
//   caseType: "RERA",
//   caseYear:
//   fullDetails.caseDetails?.year ||
//   filingNo.slice(-4) ||
//   "",
// caseFiledDate:
//   fullDetails.caseDetails?.filing_date ||
//   fullDetails.sections
//     ?.flatMap(s => Object.values(s.data || {}))
//     ?.find(v => typeof v === "string" && v.includes("/")) ||
//   "",


//   court: "RERA",
//   courtName: "RERA",

//   petitionerName,
//   respondentName,
//   nextHearingDate,


//   status: caseStatus,

//   caseHistory: normalizedHistory,


//   allPetitioners:
//   fullDetails.sections
//     ?.flatMap(s => s.data?.Petitioner || [])
//     ?.map(name => ({ name })) ||
//   [{ name: petitionerName }],

// allRespondents:
//   fullDetails.sections
//     ?.flatMap(s => s.data?.Respondent || [])
//     ?.map(name => ({ name })) ||
//   [{ name: respondentName }],

//   petitionerAdvocate:
//     fullDetails.sections?.[0]?.data?.["Petitioner Advocate"] || "",

//   respondentAdvocate:
//     fullDetails.sections?.[0]?.data?.["Respondent Advocate"] || "",

//   allPetitionerAdvocates:
//     fullDetails.sections
//       ?.flatMap(s => s.data?.["Petitioner Advocate"] || [])
//       ?.map(name => ({ name })) || [],

//   allRespondentAdvocates:
//     fullDetails.sections
//       ?.flatMap(s => s.data?.["Respondent Advocate"] || [])
//       ?.map(name => ({ name })) || [],

//   // 🔹 RAW RERA DATA (DO NOT REMOVE)
//   sections: fullDetails.sections || [],
//   orders: fullDetails.orders || [],

//   // 🔹 OWNERSHIP
//   owner: user.uid,
//   team: [user.uid],

//   // 🔹 PAYLOAD (DEBUG / FUTURE USE)
//   payload: {
//     source: "RERA",
//     filingNo,
//     fullDetails,
//     exportedAt: new Date().toISOString(),
//   },

//   files: [],
//   casenotes: "",
// };




successfulExports++;
if (targetCollection === "pending") currentPendingCount++;

    setExportProgress(p => ({
      ...p,
      exported: p.exported + 1,
      currentCase: `Completed: ${filingNo}`
    }));

  } catch (err) {
    console.error("Export error:", err);
    setExportProgress(p => ({
      ...p,
      exported: p.exported + 1,
      failedCases: [...p.failedCases, filingNo]
    }));
  }
}


    await new Promise(r => setTimeout(r, 400));

    if (successfulExports > 0) {
      setIsInfoModalOpen(true);
      if (duplicateCases.length > 0) {
        toast.success(`Exported ${successfulExports} case(s). Skipped ${duplicateCases.length} duplicate(s).`);
      } else {
        toast.success(`Exported ${successfulExports} RERA case(s) successfully`);
      }

    } else if (duplicateCases.length > 0) {
      toast("All selected cases were already exported.", { icon: "⚠️" });

    } else {
      toast.error("No cases were exported successfully.");
    }

  } catch (error) {
    console.error("Export error:", error);
    toast.error(`Error exporting cases: ${error.message}`);
  } finally {
    setExporting(false);
    setExportProgress({
      total: 0,
      exported: 0,
      currentCase: null,
      startTime: null,
      failedCases: []
    });
  }
};

const stateCode = location.state?.searchParams?.state_code || "UP";

const renderCell = (value) => {
  if (value == null) return "—";

  // ✅ View button (UP or Haryana)
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    (value.filingNo || value.internalId)
  ) {
    return (
      <button
        type="button"
        className="text-blue-600 underline hover:text-blue-700"
        onClick={() => {
          if (stateCode === "HARYANA") {
            const sessionId =
              sessionStorage.getItem("haryanaReraSessionId") ||
              localStorage.getItem("haryanaReraSessionId");
            if (!sessionId) {
              toast.error("Session expired. Please search again.");
              return;
            }
            navigate(`/portal/rera/caseDetails/${value.filingNo || value.internalId}`, {
              state: {
                stateCode: "HARYANA",
                internalId: value.internalId,
                caseTitle: "",
              },
            });
          } else {
            const sessionId =
              sessionStorage.getItem("upReraSessionId") ||
              localStorage.getItem("upReraSessionId");
            if (!sessionId) {
              toast.error("Session expired. Please search again.");
              return;
            }
            sessionStorage.setItem("upReraLastFiling", value.filingNo);
            navigate(`/portal/rera/caseDetails/${value.filingNo}`);
          }
        }}
      >
        {value.text || "View"}
      </button>
    );
  }

  // ✅ PDF arrays
  if (Array.isArray(value)) {
    return <PdfLinksCell value={value} />;
  }

  // ✅ Object fallback
  if (typeof value === "object") {
    return (
      <div className="flex flex-col gap-1">
        {Object.values(value).map((v, i) => (
          <div key={i}>{String(v)}</div>
        ))}
      </div>
    );
  }

  // ✅ Multiline string
  if (typeof value === "string" && value.includes("\n")) {
    return value.split("\n").map((v, i) => <div key={i}>{v}</div>);
  }

  return value;
};






useEffect(() => {
  console.log("HEADERS:", headers);
  console.log("FIRST ROW:", cases[0]);
}, [headers, cases]);

useEffect(() => {
  // Add a page-specific class to the body
  document.body.classList.add("account-page-light-override");

  return () => {
    // Remove it when leaving the page
    document.body.classList.remove("account-page-light-override");
  };
}, []);
const [lightMode, setLightMode] = useState(() => {
        const storedLightMode = localStorage.getItem('lightMode');
        const storedTheme = localStorage.getItem('theme');
        if (storedLightMode !== null) {
          return storedLightMode === 'true';
        }
        if (storedTheme) {
          return storedTheme === 'light';
        }
        return false;
      });
  
      useEffect(() => {
    const handler = () => {
      setLightMode(localStorage.getItem("theme") === "light");
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <p className="text-slate-500 text-lg">Loading RERA case data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-slate-500 text-lg">No case data available</p>
            <Link to="/portal/fetchfromrera" className="text-blue-600 hover:underline mt-2 inline-block">
              Return to Search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    

      <div className="min-h-screen bg-[#F8FAFC]">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          {/* Breadcrumb */}
          <div className="mb-4 flex items-center gap-1.5 text-sm">
            <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <Link to="/portal/fetchfromrera" className="text-slate-500 hover:text-slate-800">RERA</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">Results</span>
          </div>

          {/* Stepper */}
          <div className="mb-4"><FindCaseStepper current={3} /></div>

          {/* Summary stat cards */}
          {(caseData?.rows?.length || 0) > 0 && (
            <div className="mb-4 grid grid-cols-2 gap-4">
              {[
                { label: "Total Found", value: caseData?.rows?.length || 0, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
                { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
              ].map(({ label, value, Icon, tint }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                    <p className="truncate text-xs text-slate-500">{label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Export Progress Modal */}
        {exporting && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Exporting RERA Cases</h3>
                <p className="text-sm text-gray-600 mb-6">Please wait...</p>
                <div className="relative mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all"
                      style={{ width: `${(exportProgress.exported / exportProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold">
                      {Math.round((exportProgress.exported / exportProgress.total) * 100)}%
                    </span>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm">Cases Exported:</span>
                    <span className="font-bold">{exportProgress.exported} / {exportProgress.total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

          {/* Cases Table */}
          <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {(caseData?.rows?.length || 0) === 0 ? (
              <div className="py-12 text-center">
                <svg className="mx-auto mb-4 h-16 w-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium text-slate-700">No records found</p>
                <p className="mt-1 text-sm text-slate-400">
                  We couldn't find any cases matching your search
                </p>
                <Link
                  to="/portal/fetchfromrera"
                  className="mt-4 inline-block text-sm font-medium text-yellow-600 hover:underline"
                >
                  Back to Search
                </Link>
              </div>
            ) : (
              <>
                {/* Toolbar — search + export */}
                <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search in results..."
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                    />
                    {searchText && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchText("");
                          setCurrentPage(1);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                        title="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-3">
                    {selectedCases.size > 0 && (
                      <span className="text-sm text-slate-500">{selectedCases.size} selected</span>
                    )}
                    <button
                      onClick={exportSelectedCases}
                      className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                        exporting || selectedCases.size === 0
                          ? "cursor-not-allowed bg-slate-100 text-slate-400"
                          : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
                      }`}
                      disabled={exporting || selectedCases.size === 0}
                    >
                      <Download className="h-4 w-4" />
                      {exporting
                        ? `Exporting (${exportProgress.exported}/${exportProgress.total})`
                        : `Export Selected (${selectedCases.size})`}
                    </button>
                  </div>
                </div>

                {cases.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-base font-medium text-slate-700">No cases match your search</p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchText("");
                        setCurrentPage(1);
                      }}
                      className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table w-full border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          <th className="w-12 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                              title="Select all"
                            />
                          </th>
                          {headers.map((h, i) => (
                            <th
                              key={i}
                              className={
                                normalizeKey(h) === "action"
                                  ? "w-16 px-4 py-3 text-center"
                                  : "px-4 py-3"
                              }
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {currentCases.map((row, idx) => {
                          const rIdx = indexOfFirstCase + idx;
                          const isSelected = selectedCases.has(rIdx);
                          return (
                            <tr
                              key={rIdx}
                              className={`border-b border-slate-100 transition-colors duration-150 ${
                                isSelected ? "bg-yellow-50/60" : "hover:bg-slate-50"
                              }`}
                            >
                              <td className="px-4 py-3.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleCaseSelect(rIdx)}
                                  className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                                />
                              </td>

                              {headers.map((h, cIdx) => {
                                let value;
                                if (normalizeKey(h) === "action") {
                                  value = row.Action;
                                } else {
                                  value =
                                    row[h] ??
                                    row[
                                      Object.keys(row).find(
                                        (k) =>
                                          normalizeKey(h).includes(normalizeKey(k)) ||
                                          normalizeKey(k).includes(normalizeKey(h))
                                      )
                                    ];
                                }

                                const isPdfColumn =
                                  normalizeKey(h).includes("notice") ||
                                  normalizeKey(h).includes("order");
                                const isActionColumn =
                                  normalizeKey(h) === "action" &&
                                  (value?.filingNo || value?.internalId);

                                const tdClass = isActionColumn
                                  ? "px-4 py-3.5 text-center"
                                  : `px-4 py-3.5 text-sm ${
                                      cIdx === 0 ? "font-medium text-slate-900" : "text-slate-600"
                                    }`;

                                return (
                                  <td key={cIdx} className={tdClass}>
                                    {isActionColumn ? (
                                      <button
                                        type="button"
                                        title="View case details"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                                        onClick={() => {
                                          if (stateCode === "HARYANA") {
                                            const sessionId =
                                              sessionStorage.getItem("haryanaReraSessionId") ||
                                              localStorage.getItem("haryanaReraSessionId");
                                            if (!sessionId) {
                                              toast.error("Session expired. Please search again.");
                                              return;
                                            }
                                            const caseTitle = `${row["Complainant"] || ""} VS ${row["Respondent"] || ""}`;
                                            navigate(`/portal/rera/caseDetails/${value.filingNo || value.internalId}`, {
                                              state: {
                                                stateCode: "HARYANA",
                                                internalId: value.internalId,
                                                caseTitle,
                                              },
                                            });
                                          } else {
                                            const sessionId =
                                              sessionStorage.getItem("upReraSessionId") ||
                                              localStorage.getItem("upReraSessionId");
                                            if (!sessionId) {
                                              toast.error("Session expired. Please search again.");
                                              return;
                                            }
                                            sessionStorage.setItem("upReraLastFiling", value.filingNo);
                                            const caseTitle = row["Case Title"] || row.caseTitle || "";
                                            navigate(`/portal/rera/caseDetails/${value.filingNo}`, {
                                              state: { caseTitle },
                                            });
                                          }
                                        }}
                                      >
                                        <Eye className="h-4 w-4" />
                                      </button>
                                    ) : isPdfColumn ? (
                                      <PdfLinksCell value={value} />
                                    ) : (
                                      renderCell(value)
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Footer — result range + pagination */}
                {cases.length > 0 && (
                  <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                    <p className="text-sm text-slate-500">
                      Showing {indexOfFirstCase + 1} to {Math.min(indexOfLastCase, cases.length)} of{" "}
                      {cases.length} results
                    </p>

                    {totalPages > 1 && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                          title="Previous page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>

                        {currentPage > 3 && (
                          <>
                            <button
                              onClick={() => handlePageChange(1)}
                              className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              1
                            </button>
                            {currentPage > 4 && <span className="px-1 text-slate-400">...</span>}
                          </>
                        )}

                        {getPageNumbers().map((number) => (
                          <button
                            key={number}
                            onClick={() => handlePageChange(number)}
                            className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                              currentPage === number
                                ? "bg-yellow-500 text-slate-900"
                                : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {number}
                          </button>
                        ))}

                        {currentPage < totalPages - 2 && (
                          <>
                            {currentPage < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                            <button
                              onClick={() => handlePageChange(totalPages)}
                              className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}

                        <button
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                          title="Next page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={closeInfoModal}
        title="Export Successful"
        message="All selected RERA cases exported successfully!"
      />
    </>
  );
}

export default ReraCaseList;